import { supabase, TABLES } from '../lib/supabase';
import { getDefaultDomain, getLinkDomain } from '../utils/domainUtils';

/**
 * Supabase service for ShortLink application
 * Clean, reliable backend with PostgreSQL
 */
class SupabaseService {
  constructor() {
    console.log('🟢 SupabaseService initialized - PostgreSQL backend');
  }

  // ==================== AUTHENTICATION ====================

  /**
   * Simple email/password login
   */
  async signIn(email, password) {
    try {
      console.log('🔐 Signing in user:', email);

      if (email === process.env.REACT_APP_ADMIN_EMAIL && password === process.env.REACT_APP_ADMIN_PASSWORD) {
        // Return admin user info
        return {
          user: {
            id: crypto.randomUUID(),
            email: process.env.REACT_APP_ADMIN_EMAIL,
            firstName: 'Admin',
            lastName: 'User',
            role: 'ADMIN'
          },
          session: { access_token: 'demo-token' },
          error: null
        };
      }

      // For real authentication, use Supabase auth
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      return { user: data.user, session: data.session, error: null };

    } catch (error) {
      console.error('❌ Sign in error:', error);
      return { user: null, session: null, error: error.message };
    }
  }

  /**
   * Sign out user
   */
  async signOut() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('❌ Sign out error:', error);
      return { error: error.message };
    }
  }

  // ==================== DOMAINS ====================

  /**
   * Load all domains for user (public access for now)
   */
  async loadDomains(userId = null) {
    try {
      console.log('🌐 Loading domains from Supabase for user:', userId);

      // Load all domains without owner filtering (simplified for testing)
      const { data, error } = await supabase
        .from(TABLES.DOMAINS)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Supabase error loading domains:', error);
        console.error('Error details:', error);
        return []; // Return empty array instead of throwing
      }

      console.log('✅ Domains loaded from Supabase:', data?.length || 0);
      return (data || []).map(domain => this.formatDomain(domain));

    } catch (error) {
      console.error('❌ Error loading domains:', error);
      return [];
    }
  }

  /**
   * Create new domain
   */
  async createDomain(domainData, userId = null) {
    try {
      console.log('➕ Creating domain in Supabase:', domainData);

      const verificationToken = this.generateVerificationToken();

      // For testing, create domain without owner constraint
      const domainRecord = {
        domain: domainData.domain,
        redirect_type: domainData.redirectType || 'TEMPORARY',
        is_verified: false,
        verification_token: verificationToken,
        verification_instructions: this.getDNSInstructions(domainData.domain, verificationToken)
        // Removed owner field temporarily to avoid UUID constraint issues
      };

      const { data, error } = await supabase
        .from(TABLES.DOMAINS)
        .insert([domainRecord])
        .select()
        .single();

      if (error) {
        console.error('❌ Supabase error creating domain:', error);
        console.error('Error details:', error);
        throw new Error(`Failed to create domain: ${error.message}`);
      }

      console.log('✅ Domain created in Supabase:', data);
      return this.formatDomain(data);

    } catch (error) {
      console.error('❌ Error creating domain:', error);
      throw error;
    }
  }

  /**
   * Verify domain with real DNS checking
   */
  async verifyDomain(domain) {
    try {
      console.log('🔍 Verifying domain via DNS:', domain.domain);

      // Real DNS verification using Cloudflare DNS-over-HTTPS
      const dnsQuery = `https://cloudflare-dns.com/dns-query?name=_amplify-verification.${domain.domain}&type=TXT`;

      const response = await fetch(dnsQuery, {
        method: 'GET',
        headers: { 'Accept': 'application/dns-json' }
      });

      if (!response.ok) {
        throw new Error(`DNS query failed: ${response.status}`);
      }

      const dnsData = await response.json();
      console.log('📋 DNS Response:', dnsData);

      let isVerified = false;
      const foundRecords = [];

      if (dnsData.Answer && dnsData.Answer.length > 0) {
        for (const record of dnsData.Answer) {
          if (record.type === 16) { // TXT record
            const txtValue = record.data.replace(/"/g, '');
            foundRecords.push(txtValue);

            if (txtValue === domain.verification_token || txtValue === domain.verificationToken) {
              isVerified = true;
              console.log('✅ Verification token found in DNS!');
              break;
            }
          }
        }
      }

      // Update domain verification status in Supabase
      if (isVerified) {
        const { error } = await supabase
          .from(TABLES.DOMAINS)
          .update({
            is_verified: true,
            updated_at: new Date().toISOString()
          })
          .eq('id', domain.id);

        if (error) {
          console.error('❌ Error updating domain verification:', error);
        } else {
          console.log('✅ Domain verification updated in Supabase');
        }
      }

      return {
        isVerified,
        domain: domain.domain,
        token: domain.verification_token || domain.verificationToken,
        foundRecords,
        timestamp: new Date().toISOString()
      };

    } catch (error) {
      console.error('❌ Domain verification error:', error);
      return {
        isVerified: false,
        error: error.message,
        domain: domain.domain
      };
    }
  }

  // ==================== LINKS ====================

  /**
   * Create new link
   */
  async createLink(linkData, userId = null) {
    try {
      console.log('🔗 Creating link in Supabase:', linkData);
      console.log('🖼️ SupabaseService: Image URL received:', linkData.imageUrl);
      console.log('📝 SupabaseService: Description received:', linkData.description);

      // Generate short code
      const shortCode = linkData.customSlug || this.generateShortCode();

      // Get domain name dynamically
      const domains = await this.loadDomains(userId);
      let domainName = getDefaultDomain(domains);

      if (linkData.domainId) {
        const domain = domains.find(d => d.id === linkData.domainId);
        if (domain && domain.isVerified) {
          domainName = domain.domain;
        }
      }

      const linkRecord = {
        title: linkData.title || `Link to ${linkData.originalUrl}`,
        original_url: linkData.originalUrl,
        short_code: shortCode,
        full_short_url: `https://${domainName}/${shortCode}`,
        domain_id: linkData.domainId || null,
        domain_name: domainName,
        // Removed owner to avoid UUID constraint issues
        click_count: 0,
        is_active: true,
        tags: Array.isArray(linkData.tags) ? linkData.tags : (linkData.tags ? linkData.tags.split(',').map(t => t.trim()) : []),
        description: linkData.description || null,
        image_url: linkData.imageUrl || null
      };

      console.log('💾 SupabaseService: Link record to insert:', linkRecord);
      console.log('📊 SupabaseService: Metadata fields:', {
        description: linkRecord.description,
        image_url: linkRecord.image_url,
        title: linkRecord.title
      });

      const { data, error } = await supabase
        .from(TABLES.LINKS)
        .insert([linkRecord])
        .select()
        .single();

      if (error) {
        console.error('❌ Supabase error creating link:', error);

        // Handle specific error types with user-friendly messages
        if (error.code === '23505' && error.message.includes('short_code')) {
          throw new Error(`The slug "${shortCode}" is already taken. Please choose a different one.`);
        }

        if (error.code === '23505') {
          throw new Error('This link already exists. Please try a different URL or slug.');
        }

        if (error.message.includes('constraint')) {
          throw new Error('Invalid data provided. Please check your input and try again.');
        }

        throw new Error(error.message || 'Failed to create link');
      }

      console.log('✅ Link created in Supabase:', data);
      return this.formatLink(data);

    } catch (error) {
      console.error('❌ Error creating link:', error);
      throw error;
    }
  }

  /**
   * Load user links
   */
  async getUserLinks(userId = null) {
    try {
      console.log('🔗 Loading links from Supabase for user:', userId);

      const { data, error } = await supabase
        .from(TABLES.LINKS)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Supabase error loading links:', error);
        throw error;
      }

      console.log('✅ Links loaded from Supabase:', data.length);
      return data.map(link => this.formatLink(link));

    } catch (error) {
      console.error('❌ Error loading links:', error);
      return [];
    }
  }

  /**
   * Get links with options (for compatibility)
   */
  async getLinks(userId, options = {}) {
    try {
      const links = await this.getUserLinks(userId);

      // Apply search filter if provided
      let filteredLinks = links;
      if (options.search) {
        const searchLower = options.search.toLowerCase();
        filteredLinks = links.filter(link =>
          link.title?.toLowerCase().includes(searchLower) ||
          link.originalUrl?.toLowerCase().includes(searchLower) ||
          link.shortCode?.toLowerCase().includes(searchLower)
        );
      }

      // Apply domain filter if provided
      if (options.domain && options.domain !== 'all') {
        filteredLinks = filteredLinks.filter(link => link.domainId === options.domain);
      }

      // Apply tags filter if provided
      if (options.tags && options.tags.length > 0) {
        filteredLinks = filteredLinks.filter(link =>
          options.tags.some(tag => link.tags?.includes(tag))
        );
      }

      // Apply status filter if provided
      if (options.status && options.status !== 'all') {
        filteredLinks = filteredLinks.filter(link => {
          if (options.status === 'active') return link.isActive;
          if (options.status === 'inactive') return !link.isActive;
          if (options.status === 'expired') return link.expiresAt && new Date(link.expiresAt) < new Date();
          return true;
        });
      }

      // Apply sorting
      if (options.sortBy) {
        filteredLinks.sort((a, b) => {
          let aVal, bVal;
          switch (options.sortBy) {
            case 'created':
              aVal = new Date(a.createdAt);
              bVal = new Date(b.createdAt);
              break;
            case 'clicks':
              aVal = a.clickCount || 0;
              bVal = b.clickCount || 0;
              break;
            case 'title':
              aVal = a.title?.toLowerCase() || '';
              bVal = b.title?.toLowerCase() || '';
              break;
            default:
              aVal = new Date(a.createdAt);
              bVal = new Date(b.createdAt);
          }

          if (options.sortOrder === 'asc') {
            return aVal > bVal ? 1 : -1;
          } else {
            return aVal < bVal ? 1 : -1;
          }
        });
      }

      // Apply pagination
      const page = options.page || 1;
      const limit = options.limit || 20;
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;

      const paginatedLinks = filteredLinks.slice(startIndex, endIndex);
      const totalPages = Math.ceil(filteredLinks.length / limit);
      const hasMore = page < totalPages;

      return {
        links: paginatedLinks,
        page,
        totalPages,
        total: filteredLinks.length,
        hasMore
      };

    } catch (error) {
      console.error('❌ Error getting links:', error);
      return {
        links: [],
        page: 1,
        totalPages: 1,
        total: 0,
        hasMore: false
      };
    }
  }

  /**
   * Get user statistics (for Dashboard)
   */
  async getUserStats(userId) {
    try {
      const links = await this.getUserLinks(userId);
      const totalClicks = links.reduce((sum, link) => sum + (link.clickCount || 0), 0);

      return {
        totalLinks: links.length,
        totalClicks: totalClicks,
        clicksToday: 0, // Would need analytics calculation
        conversionRate: links.length > 0 ? (totalClicks / links.length) * 100 : 0
      };
    } catch (error) {
      console.error('❌ Error getting user stats:', error);
      return {
        totalLinks: 0,
        totalClicks: 0,
        clicksToday: 0,
        conversionRate: 0
      };
    }
  }

  /**
   * Get user tags (for useLinks hook)
   */
  async getUserTags(userId) {
    try {
      const links = await this.getUserLinks(userId);
      const allTags = links.flatMap(link => link.tags || []);
      const uniqueTags = [...new Set(allTags)];
      return uniqueTags;
    } catch (error) {
      console.error('❌ Error getting user tags:', error);
      return [];
    }
  }


  /**
   * Delete link
   */
  async deleteLink(linkId) {
    try {
      const { error } = await supabase
        .from(TABLES.LINKS)
        .delete()
        .eq('id', linkId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('❌ Error deleting link:', error);
      throw error;
    }
  }

  /**
   * Get link by ID
   */
  async getLinkById(linkId) {
    try {
      const { data, error } = await supabase
        .from(TABLES.LINKS)
        .select('*')
        .eq('id', linkId)
        .single();

      if (error) throw error;
      return this.formatLink(data);
    } catch (error) {
      console.error('❌ Error getting link:', error);
      return null;
    }
  }

  /**
   * Get link by short code
   */
  async getByShortCode(shortCode) {
    try {
      const { data, error } = await supabase
        .from(TABLES.LINKS)
        .select('*')
        .eq('short_code', shortCode)
        .single();

      if (error) throw error;
      return this.formatLink(data);
    } catch (error) {
      console.error('❌ Error getting link by short code:', error);
      return null;
    }
  }

  /**
   * Increment click count
   */
  async incrementClicks(linkId) {
    try {
      // First get the current click count
      const { data: linkData, error: selectError } = await supabase
        .from(TABLES.LINKS)
        .select('click_count')
        .eq('id', linkId)
        .single();

      if (selectError) throw selectError;

      // Increment the click count
      const newClickCount = (linkData.click_count || 0) + 1;

      const { data, error } = await supabase
        .from(TABLES.LINKS)
        .update({
          click_count: newClickCount
        })
        .eq('id', linkId)
        .select()
        .single();

      if (error) throw error;
      console.log('✅ Click count incremented to:', newClickCount);
      return this.formatLink(data);
    } catch (error) {
      console.error('❌ Error incrementing clicks:', error);
      throw error;
    }
  }

  // ==================== ANALYTICS ====================

  /**
   * Record analytics event
   */
  async recordAnalytics(linkId, analyticsData) {
    try {
      const { data, error } = await supabase
        .from(TABLES.ANALYTICS)
        .insert([{
          link_id: linkId,
          ip_address: analyticsData.ipAddress,
          user_agent: analyticsData.userAgent,
          referrer: analyticsData.referrer,
          country: analyticsData.country,
          region: analyticsData.region,
          city: analyticsData.city,
          device: analyticsData.device,
          browser: analyticsData.browser,
          os: analyticsData.os,
          created_at: new Date().toISOString()
        }]);

      if (error) throw error;

      console.log('✅ Analytics event recorded for link:', linkId);
      return data;

    } catch (error) {
      console.error('❌ Error recording analytics:', error);
      // Don't throw error for analytics failure - it shouldn't block redirects
      return null;
    }
  }

  /**
   * Check SSL status for a domain
   */
  async checkSSLStatus(domain) {
    try {
      console.log('🔍 Checking SSL status for:', domain);

      // Try to fetch the domain with HTTPS
      const response = await fetch(`https://${domain}`, {
        method: 'HEAD',
        mode: 'no-cors' // Avoid CORS issues
      });

      // If we can connect to HTTPS, SSL is likely working
      return {
        sslEnabled: true,
        sslStatus: 'issued',
        checkedAt: new Date().toISOString()
      };

    } catch (error) {
      console.log('❌ SSL check failed for:', domain, error.message);

      return {
        sslEnabled: false,
        sslStatus: 'failed',
        checkedAt: new Date().toISOString()
      };
    }
  }

  /**
   * Update domain SSL status
   */
  async updateDomainSSL(domainId, sslData) {
    try {
      const { data, error } = await supabase
        .from(TABLES.DOMAINS)
        .update({
          ssl_enabled: sslData.sslEnabled,
          ssl_status: sslData.sslStatus,
          ssl_issued_at: sslData.sslEnabled ? new Date().toISOString() : null
        })
        .eq('id', domainId)
        .select()
        .single();

      if (error) throw error;
      return this.formatDomain(data);
    } catch (error) {
      console.error('❌ Error updating domain SSL:', error);
      throw error;
    }
  }

  /**
   * Delete domain
   */
  async deleteDomain(domainId) {
    try {
      console.log('🗑️ Deleting domain from database:', domainId);

      const { error } = await supabase
        .from(TABLES.DOMAINS)
        .delete()
        .eq('id', domainId);

      if (error) {
        console.error('❌ Supabase error deleting domain:', error);
        throw new Error(error.message || 'Failed to delete domain');
      }

      console.log('✅ Domain deleted from database successfully');
      return true;
    } catch (error) {
      console.error('❌ Error deleting domain:', error);
      throw error;
    }
  }

  /**
   * Update an existing link
   */
  async updateLink(linkId, updates, userId = null) {
    try {
      console.log('🔄 Updating link:', linkId, 'with:', updates);

      const { data, error } = await supabase
        .from(TABLES.LINKS)
        .update(updates)
        .eq('id', linkId)
        .select()
        .single();

      if (error) {
        console.error('❌ Supabase error updating link:', error);
        throw new Error(error.message || 'Failed to update link');
      }

      console.log('✅ Link updated successfully:', data);
      return this.formatLink(data);
    } catch (error) {
      console.error('❌ Error updating link:', error);
      throw error;
    }
  }

  /**
   * Get analytics data for all user links
   */
  async getUserAnalytics(userId = null, days = 30) {
    try {
      console.log('📈 Getting user analytics for days:', days);

      // Get date range
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      // Get all user links
      const userLinks = await this.getUserLinks(userId);
      const linkIds = userLinks.map(link => link.id);

      if (linkIds.length === 0) {
        return {
          totalClicks: 0,
          clicksToday: 0,
          topLinks: [],
          clicksByDate: [],
          clicksByCountry: [],
          clicksByDevice: [],
          clicksByReferrer: []
        };
      }

      // Get analytics for all user links
      const { data: analytics, error } = await supabase
        .from(TABLES.ANALYTICS)
        .select('*')
        .in('link_id', linkIds)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      console.log('📊 Analytics events found:', analytics.length);

      // Process analytics data
      const totalClicks = analytics.length;

      // Get today's clicks
      const today = new Date().toISOString().split('T')[0];
      const clicksToday = analytics.filter(event =>
        event.created_at.startsWith(today)
      ).length;

      // Group by date for time series
      const clicksByDate = this.groupAnalyticsByDate(analytics);

      // Group by country
      const clicksByCountry = this.groupAnalyticsByField(analytics, 'country');

      // Group by device
      const clicksByDevice = this.groupAnalyticsByField(analytics, 'device');

      // Group by referrer
      const clicksByReferrer = this.groupAnalyticsByField(analytics, 'referrer');

      // Get top performing links
      const topLinks = this.getTopLinks(userLinks, analytics);

      return {
        totalClicks,
        clicksToday,
        topLinks,
        clicksByDate,
        clicksByCountry,
        clicksByDevice,
        clicksByReferrer
      };

    } catch (error) {
      console.error('❌ Error getting user analytics:', error);
      return {
        totalClicks: 0,
        clicksToday: 0,
        topLinks: [],
        clicksByDate: [],
        clicksByCountry: [],
        clicksByDevice: [],
        clicksByReferrer: []
      };
    }
  }

  /**
   * Get analytics for a specific link
   */
  async getLinkAnalytics(linkId, days = 30) {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data: analytics, error } = await supabase
        .from(TABLES.ANALYTICS)
        .select('*')
        .eq('link_id', linkId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      return {
        totalClicks: analytics.length,
        clicksByDate: this.groupAnalyticsByDate(analytics),
        clicksByCountry: this.groupAnalyticsByField(analytics, 'country'),
        clicksByDevice: this.groupAnalyticsByField(analytics, 'device'),
        clicksByReferrer: this.groupAnalyticsByField(analytics, 'referrer')
      };

    } catch (error) {
      console.error('❌ Error getting link analytics:', error);
      return {
        totalClicks: 0,
        clicksByDate: [],
        clicksByCountry: [],
        clicksByDevice: [],
        clicksByReferrer: []
      };
    }
  }

  /**
   * Helper: Group analytics by date
   */
  groupAnalyticsByDate(analytics) {
    const grouped = {};

    analytics.forEach(event => {
      const date = event.created_at.split('T')[0];
      grouped[date] = (grouped[date] || 0) + 1;
    });

    return Object.entries(grouped).map(([date, clicks]) => ({
      date,
      clicks
    })).sort((a, b) => a.date.localeCompare(b.date));
  }

  /**
   * Helper: Group analytics by field
   */
  groupAnalyticsByField(analytics, field) {
    const grouped = {};

    analytics.forEach(event => {
      const value = event[field] || 'Unknown';
      grouped[value] = (grouped[value] || 0) + 1;
    });

    return Object.entries(grouped).map(([name, clicks]) => ({
      name,
      clicks
    })).sort((a, b) => b.clicks - a.clicks);
  }

  /**
   * Helper: Get top performing links
   */
  getTopLinks(links, analytics) {
    const linkClicks = {};

    analytics.forEach(event => {
      linkClicks[event.link_id] = (linkClicks[event.link_id] || 0) + 1;
    });

    return links
      .map(link => ({
        ...link,
        analyticsClicks: linkClicks[link.id] || 0
      }))
      .sort((a, b) => b.analyticsClicks - a.analyticsClicks)
      .slice(0, 10);
  }

  // ==================== UTILITY FUNCTIONS ====================

  /**
   * Generate verification token
   */
  generateVerificationToken() {
    return 'supabase-verify-' + Math.random().toString(36).substring(2, 18);
  }

  /**
   * Generate short code
   */
  generateShortCode(length = 6) {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * Get DNS setup instructions
   */
  getDNSInstructions(domain, token) {
    return `Add this TXT record to your DNS settings:

Host/Name: _amplify-verification.${domain}
Value: ${token}
TTL: 300 (or default)

After adding the record, click 'Verify Domain' to complete the setup.`;
  }

  /**
   * Format domain object for frontend compatibility
   */
  formatDomain(domain) {
    return {
      id: domain.id,
      domain: domain.domain,
      isVerified: domain.is_verified,
      verificationToken: domain.verification_token,
      verificationInstructions: domain.verification_instructions,
      redirectType: domain.redirect_type,
      defaultDestinationUrl: domain.default_destination_url,
      sslEnabled: domain.ssl_enabled,
      sslStatus: domain.ssl_status,
      sslCertificateArn: domain.ssl_certificate_arn,
      sslIssuedAt: domain.ssl_issued_at,
      sslExpiresAt: domain.ssl_expires_at,
      owner: domain.owner,
      createdAt: domain.created_at,
      updatedAt: domain.updated_at
    };
  }

  /**
   * Format link object for frontend compatibility
   */
  formatLink(link) {
    return {
      id: link.id,
      title: link.title,
      originalUrl: link.original_url,
      shortCode: link.short_code,
      fullShortUrl: link.full_short_url,
      domainId: link.domain_id,
      domain: link.domain_name,
      owner: link.owner,
      clickCount: link.click_count,
      isActive: link.is_active,
      expiresAt: link.expires_at,
      utmSource: link.utm_source,
      utmMedium: link.utm_medium,
      utmCampaign: link.utm_campaign,
      tags: link.tags || [],
      description: link.description,
      imageUrl: link.image_url,
      createdAt: link.created_at,
      updatedAt: link.updated_at
    };
  }
}

export default new SupabaseService();